import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiFilter, FiDownload, FiStar, FiCheckCircle, FiShield, FiZap, FiLayout, FiCpu, FiPlus } = FiIcons;

const Marketplace = () => {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  const categories = ['All', 'Cardiology', 'Oncology', 'Pediatrics', 'Ops', 'Diagnostics'];

  const items = [
    {
      id: 1,
      title: "Chest Pain Triage v4",
      category: "Cardiology",
      author: "HDAI Core",
      rating: 4.9,
      installs: "1.2k",
      price: "Free",
      verified: true,
      desc: "Validated triage logic for ER settings. Integrates Venice-Clinical for symptom analysis.",
      icon: "🫀",
      color: "bg-red-50"
    },
    {
      id: 2,
      title: "ICD-11 Auto-Coder",
      category: "Ops",
      author: "HealthTech Labs",
      rating: 4.7,
      installs: "800",
      price: "$29/mo",
      verified: true,
      desc: "Real-time ICD-11 code extraction from physician notes using HF-Mistral.",
      icon: "🔢",
      color: "bg-blue-50"
    },
    {
      id: 3,
      title: "Pediatric Fever Protocol",
      category: "Pediatrics",
      author: "Dr. Sarah J.",
      rating: 5.0,
      installs: "450",
      price: "Free",
      verified: false,
      desc: "Age-specific dosage and intervention logic based on current AAP guidelines.",
      icon: "🤒",
      color: "bg-yellow-50"
    },
    {
      id: 4,
      title: "Pathology Summarizer",
      category: "Diagnostics",
      author: "BioAI",
      rating: 4.8,
      installs: "2.1k",
      price: "$49/mo",
      verified: true,
      desc: "Compresses complex pathology reports into high-level physician summaries.",
      icon: "🔬",
      color: "bg-purple-50"
    }
  ];

  const filteredItems = items.filter(i => 
    (filter === 'All' || i.category === filter) &&
    (i.title.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-brand-ivory pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
          <div className="max-w-2xl">
            <h1 className="text-5xl font-display font-bold text-brand-indigo mb-4">Clinical Marketplace</h1>
            <p className="text-xl text-brand-slate">Discover and deploy pre-validated clinical logic packages (.hdpkg) directly into your IDE.</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input 
                type="text" 
                placeholder="Search packages..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 pr-4 py-4 bg-white border-transparent rounded-2xl text-sm focus:ring-2 focus:ring-brand-mint transition-all w-72 shadow-sm"
              />
              <SafeIcon icon={FiSearch} className="absolute left-4 top-4.5 text-gray-400" />
            </div>
            <button className="bg-brand-indigo text-white px-8 py-4 rounded-2xl font-bold flex items-center space-x-2 hover:bg-brand-slate transition-all shadow-lg">
              <SafeIcon icon={FiPlus} />
              <span>Publish</span>
            </button>
          </div>
        </div>

        {/* Categories */}
        <div className="flex space-x-3 mb-12 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all whitespace-nowrap ${filter === c ? 'bg-brand-indigo text-white shadow-md' : 'bg-white text-brand-indigo hover:bg-brand-indigo/5'}`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                whileHover={{ y: -8 }}
                className="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-sm hover:shadow-2xl transition-all group"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className={`w-16 h-16 ${item.color} rounded-2xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform`}>
                    {item.icon}
                  </div>
                  <div className="flex flex-col items-end">
                    {item.verified && (
                      <div className="flex items-center space-x-1 text-brand-mint mb-2">
                        <SafeIcon icon={FiCheckCircle} className="text-xs" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Verified</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-1 text-brand-indigo font-bold">
                      <SafeIcon icon={FiStar} className="text-yellow-400 text-sm" />
                      <span>{item.rating}</span>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-display font-bold text-brand-indigo mb-2 group-hover:text-brand-mint transition-colors">{item.title}</h3>
                <p className="text-brand-slate text-sm mb-6 line-clamp-2 leading-relaxed">{item.desc}</p>
                
                <div className="flex items-center justify-between pt-6 border-t border-gray-50">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Pricing</span>
                    <span className="font-bold text-brand-indigo">{item.price}</span>
                  </div>
                  <button className="flex items-center space-x-2 bg-brand-ivory text-brand-indigo px-5 py-2.5 rounded-xl font-bold hover:bg-brand-indigo hover:text-white transition-all">
                    <SafeIcon icon={FiDownload} />
                    <span>Install</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Marketplace;